import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Play, Pause, Volume2, Radio } from "lucide-react";

// Real audio/video player with Web Audio analyser-driven equalizer. Shows STREAM NOT CONFIGURED when no URL.
export function LivePlayer({ live, poster }) {
  const mediaRef = useRef(null);
  const canvasRef = useRef(null);
  const audioCtx = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [err, setErr] = useState(null);
  const url = live?.stream_url;
  const isVideo = url && !/\.(mp3|aac|m4a|ogg|wav)(\?|$)/i.test(url) && !/icecast|shoutcast|\/stream$/i.test(url);

  useEffect(() => {
    const el = mediaRef.current; if (!el || !url) return;
    let hls;
    if (/\.m3u8(\?|$)/i.test(url) && Hls.isSupported()) { hls = new Hls(); hls.loadSource(url); hls.attachMedia(el); hls.on(Hls.Events.ERROR, (_, d) => { if (d.fatal) setErr("Stream unavailable"); }); }
    else el.src = url;
    return () => hls?.destroy();
  }, [url]);

  const toggle = async () => {
    const el = mediaRef.current; if (!el) return;
    if (!audioCtx.current) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const src = ctx.createMediaElementSource(el); const an = ctx.createAnalyser(); an.fftSize = 64;
        src.connect(an); an.connect(ctx.destination); audioCtx.current = { ctx, an };
        const data = new Uint8Array(an.frequencyBinCount);
        const draw = () => { const c = canvasRef.current; if (!c) return; const g = c.getContext("2d"); an.getByteFrequencyData(data); g.clearRect(0, 0, c.width, c.height); const w = c.width / data.length; data.forEach((v, i) => { const h = (v / 255) * c.height; g.fillStyle = `rgba(232,85,42,${0.35 + v / 400})`; g.fillRect(i * w, c.height - h, w - 2, h); }); requestAnimationFrame(draw); };
        draw();
      } catch { /* cross-origin streams without CORS: play without analyser */ }
    }
    if (el.paused) { try { await audioCtx.current?.ctx.resume(); await el.play(); setPlaying(true); } catch { setErr("Playback blocked — tap again"); } }
    else { el.pause(); setPlaying(false); }
  };

  return (
    <div data-testid="live-stream-player" className="scanlines relative aspect-video rounded-xl overflow-hidden border border-[#27272A] bg-[#121216]">
      {isVideo ? <video ref={mediaRef} crossOrigin="anonymous" playsInline poster={poster} className="w-full h-full object-cover" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
        : <><img src={poster} alt="" className="w-full h-full object-cover opacity-40" /><audio ref={mediaRef} crossOrigin="anonymous" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} /></>}
      <canvas ref={canvasRef} width={640} height={120} className="absolute bottom-0 left-0 w-full h-1/3 pointer-events-none" />
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {url ? (
          <button data-testid="live-play-btn" onClick={toggle} className="w-16 h-16 rounded-full copper-btn flex items-center justify-center live-pulse">{playing ? <Pause size={24} /> : <Play size={24} className="ml-1" />}</button>
        ) : (
          <><Radio className="text-[#E8552A]" size={28} /><p className="font-display text-xl font-bold mt-3">{live ? "STREAM NOT CONFIGURED" : "No live broadcast right now"}</p><p className="mono-label text-[#71717A] mt-1">{live ? "ADMIN MUST SET A STREAM URL (HLS / ICECAST)" : "CHECK THE ARCHIVE BELOW"}</p></>
        )}
        {err && <p className="mono-label text-[#FF9F1C] mt-3">{err}</p>}
      </div>
      {url && <div className="absolute top-3 right-3 mono-label text-[#A1A1AA] flex items-center gap-1"><Volume2 size={12} /> {playing ? "ON AIR" : "PAUSED"}</div>}
    </div>
  );
}
